import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RefreshCw, CheckCircle, XCircle, Zap } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Flex, Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText, SectionLabel
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const TOOL_EMOJI = {
  send_email: '📧',
  transfer_to_agent: '🔀',
  request_human: '👤',
  end_session: '🔚',
  create_task: '📋',
  schedule_task: '⏰',
  notify_responsible: '🔔',
  jira_create_issue: '🎫',
  jira_get_issue: '🎫',
  write_memory: '🧠',
  get_calendar_events: '📅',
  create_calendar_event: '📅',
  check_free_slots: '📅',
  sheets_read_range: '📊',
  sheets_append_row: '📊',
  sheets_find_row: '📊',
};

export default function AgentActivityPage() {
  const { t, i18n } = useTranslation('agents');
  const { tenantId } = useParams();

  const toolLabel = (tool) => {
    if (!tool) return tool;
    const emoji = TOOL_EMOJI[tool];
    const label = t(`activity.tools.${tool}`, { defaultValue: '' });
    if (label) return emoji ? `${emoji} ${label}` : label;
    return tool;
  };
  const [actions, setActions]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextKey, setNextKey]       = useState(null);
  const [selected, setSelected]     = useState(null);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterTool, setFilterTool]   = useState('');
  const [filterDays, setFilterDays]   = useState('30');
  const [allAgents, setAllAgents]     = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbItems = [
    user.role === 'sallus_admin'
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : { label: t('common:home', 'Início'), path: `/dashboard/${tenantId}` },
    { label: t('activity.title') }
  ];

  const buildParams = useCallback(() => {
    const p = new URLSearchParams({ limit: '50' });
    if (filterAgent) p.set('agentId', filterAgent);
    if (filterTool)  p.set('tool', filterTool);
    if (filterDays !== 'all') p.set('days', filterDays);
    return p.toString();
  }, [filterAgent, filterTool, filterDays]);

  const load = useCallback(async () => {
    setLoading(true);
    setNextKey(null);
    setSelected(null);
    try {
      const res = await api.get(`/agents-activity/${tenantId}?${buildParams()}`);
      const data = res.data.actions || [];
      setActions(data);
      setNextKey(res.data.nextKey || null);
      setAllAgents(prev => {
        const merged = new Set([...prev, ...data.map(a => a.agentId).filter(Boolean)]);
        return [...merged];
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId, buildParams]);

  const loadMore = async () => {
    if (!nextKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get(`/agents-activity/${tenantId}?${buildParams()}&lastKey=${nextKey}`);
      const data = res.data.actions || [];
      setActions(prev => [...prev, ...data]);
      setNextKey(res.data.nextKey || null);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  useEffect(() => { load(); }, [load]);

  const allTools = [...new Set(actions.map(a => a.tool).filter(Boolean))];

  const formatInput = (input) => {
    if (!input) return '—';
    try {
      const obj = JSON.parse(input);
      // Mostra campos mais relevantes
      const keys = ['to', 'subject', 'targetAgentId', 'title', 'objective', 'name'];
      for (const k of keys) {
        if (obj[k]) return `${k}: ${String(obj[k]).slice(0, 80)}`;
      }
      return input.slice(0, 80);
    } catch { return input.slice(0, 80); }
  };

  return (
    <>
      <AppHeader title={t('activity.title')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>{t('activity.title')}</PageTitle>
            <PageSubtitle>{t('activity.subtitle')}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[2]}>
            <RefreshBtn onClick={load} disabled={loading}><RefreshCw size={13} /></RefreshBtn>
          </Flex>
        </PageHeader>

        <FilterBar>
          <FilterSelect value={filterAgent} onChange={e => setFilterAgent(e.target.value)}>
            <option value="">{t('activity.allAgents')}</option>
            {allAgents.map(a => <option key={a} value={a}>{a}</option>)}
          </FilterSelect>
          <FilterSelect value={filterTool} onChange={e => setFilterTool(e.target.value)}>
            <option value="">{t('activity.allActions')}</option>
            {allTools.map(tool => <option key={tool} value={tool}>{toolLabel(tool)}</option>)}
          </FilterSelect>
          <FilterSelect value={filterDays} onChange={e => setFilterDays(e.target.value)}>
            <option value="7">{t('activity.days', { count: 7 })}</option>
            <option value="30">{t('activity.days', { count: 30 })}</option>
            <option value="90">{t('activity.days', { count: 90 })}</option>
            <option value="all">{t('activity.allDays')}</option>
          </FilterSelect>
          <FilterCount>{t('activity.actionCount', { count: actions.length })}</FilterCount>
        </FilterBar>

        {loading ? (
          <LoadingContainer><Spinner size="24px" /></LoadingContainer>
        ) : actions.length === 0 ? (
          <EmptyState>
            <EmptyIcon><Zap size={48} strokeWidth={1} /></EmptyIcon>
            <EmptyText>{t('activity.emptyPeriod')}</EmptyText>
          </EmptyState>
        ) : (
          <Grid>
            {/* Lista */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <ListHeader>
                <Zap size={14} /> {t('activity.actionCount', { count: actions.length })}
              </ListHeader>
              <ListScroll>
                {actions.map((action, i) => (
                  <ActionItem
                    key={`${action.SK}-${i}`}
                    $active={selected?.SK === action.SK}
                    $success={action.success !== false}
                    onClick={() => setSelected(action)}
                  >
                    <Flex justify="space-between" style={{ marginBottom: 4 }}>
                      <ActionTool>
                        {action.success !== false
                          ? <CheckCircle size={12} color={theme.colors.success} />
                          : <XCircle size={12} color={theme.colors.danger} />
                        }
                        {toolLabel(action.tool)}
                      </ActionTool>
                      <ActionTime>
                        {action.createdAt ? new Date(action.createdAt).toLocaleString(i18n.language, { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}
                      </ActionTime>
                    </Flex>
                    <ActionMeta>
                      <span>{action.agentId}</span>
                      {action.contactId && <><span>·</span><span>{action.contactId}</span></>}
                    </ActionMeta>
                    {action.input && (
                      <ActionPreview>{formatInput(action.input)}</ActionPreview>
                    )}
                  </ActionItem>
                ))}
              </ListScroll>
              {nextKey && (
                <LoadMoreBtn onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? <Spinner size="13px" /> : t('activity.loadMore')}
                </LoadMoreBtn>
              )}
            </Card>

            {/* Detalhe */}
            <Card style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {selected ? (
                <>
                  <DetailHeader>
                    <Flex gap={theme.spacing[2]} align="center">
                      {selected.success !== false
                        ? <CheckCircle size={16} color={theme.colors.success} />
                        : <XCircle size={16} color={theme.colors.danger} />
                      }
                      <DetailTitle>{toolLabel(selected.tool)}</DetailTitle>
                    </Flex>
                    <DetailMeta>
                      {selected.agentId}
                      {selected.contactId && ` · ${selected.contactId}`}
                      {selected.connectorType && ` · ${selected.connectorType}`}
                      {selected.createdAt && ` · ${new Date(selected.createdAt).toLocaleString(i18n.language)}`}
                    </DetailMeta>
                  </DetailHeader>
                  <DetailScroll>
                    <Section>
                      <SectionLabel>{t('activity.params')}</SectionLabel>
                      <Pre>{selected.input ? (() => { try { return JSON.stringify(JSON.parse(selected.input), null, 2); } catch { return selected.input; } })() : '—'}</Pre>
                    </Section>
                    <Section>
                      <SectionLabel>{t('activity.result')}</SectionLabel>
                      <Pre $error={selected.success === false}>
                        {selected.result ? (() => { try { return JSON.stringify(JSON.parse(selected.result), null, 2); } catch { return selected.result; } })() : '—'}
                      </Pre>
                    </Section>
                    {selected.sessionId && (
                      <Section>
                        <SectionLabel>{t('activity.session')}</SectionLabel>
                        <Pre>{selected.sessionId}</Pre>
                      </Section>
                    )}
                  </DetailScroll>
                </>
              ) : (
                <EmptyState style={{ flex: 1 }}>
                  <EmptyIcon><Zap size={32} strokeWidth={1} /></EmptyIcon>
                  <EmptyText>{t('activity.selectAction')}</EmptyText>
                </EmptyState>
              )}
            </Card>
          </Grid>
        )}
      </PageContainer>
    </>
  );
}

const FilterBar = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; margin-bottom: ${theme.spacing[4]}; flex-wrap: wrap;`;
const FilterSelect = styled.select`padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; background: white; outline: none; cursor: pointer; &:focus { border-color: ${theme.colors.primary}; }`;
const FilterCount = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; white-space: nowrap; margin-left: auto;`;
const RefreshBtn = styled.button`display: flex; align-items: center; gap: ${theme.spacing[1]}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; background: white; cursor: pointer; font-size: ${theme.typography.sm}; &:hover { background: ${theme.colors.gray50}; } &:disabled { opacity: 0.5; }`;
const Grid = styled.div`display: grid; grid-template-columns: 380px 1fr; gap: ${theme.spacing[4]}; height: 600px;`;
const ListHeader = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.border}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary};`;
const ListScroll = styled.div`overflow-y: auto; height: calc(100% - 44px);`;
const LoadMoreBtn = styled.button`width: 100%; padding: ${theme.spacing[3]}; background: ${theme.colors.gray50}; border: none; border-top: 1px solid ${theme.colors.border}; font-size: ${theme.typography.sm}; color: ${theme.colors.primary}; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: ${theme.spacing[2]}; &:hover { background: ${theme.colors.primaryLight}; } &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const ActionItem = styled.div`padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.border}; cursor: pointer; background: ${p => p.$active ? theme.colors.primaryLight : 'white'}; border-left: 3px solid ${p => p.$active ? theme.colors.primary : p.$success === false ? theme.colors.danger : 'transparent'}; &:hover { background: ${theme.colors.gray50}; }`;
const ActionTool = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const ActionTime = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; white-space: nowrap;`;
const ActionMeta = styled.div`display: flex; gap: ${theme.spacing[1]}; font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px;`;
const ActionPreview = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const DetailHeader = styled.div`padding: ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.border}; background: white; flex-shrink: 0;`;
const DetailTitle = styled.div`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const DetailMeta = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 4px;`;
const DetailScroll = styled.div`flex: 1; overflow-y: auto; padding: ${theme.spacing[4]}; display: flex; flex-direction: column; gap: ${theme.spacing[4]};`;
const Section = styled.div``;
// SectionLabel — importado de ui.js
const Pre = styled.pre`font-size: ${theme.typography.xs}; background: ${p => p.$error ? '#fff5f5' : theme.colors.gray50}; border: 1px solid ${p => p.$error ? theme.colors.dangerBorder : theme.colors.border}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[3]}; overflow-x: auto; white-space: pre-wrap; word-break: break-word; margin: 0; font-family: monospace; color: ${p => p.$error ? theme.colors.danger : theme.colors.textPrimary};`;
